/**
 * Types mirroring the API's Pydantic schemas.
 *
 * Hand-written rather than generated, deliberately: the schema is small and
 * stable, and a hand-written file can carry the comments that explain what a
 * field means -- which is the part a generator throws away. `npm run typecheck`
 * catches shape drift at the call sites.
 */

export type ItemStatus =
  | 'not_started'
  | 'in_progress'
  | 'blocked'
  | 'done'
  | 'dropped'

export type ItemPriority = 'imminent' | 'high' | 'medium' | 'low'

/** Sort order, and the order these are offered in every menu. */
export const PRIORITIES: readonly ItemPriority[] = [
  'imminent',
  'high',
  'medium',
  'low',
]

export const STATUSES: readonly ItemStatus[] = [
  'not_started',
  'in_progress',
  'blocked',
  'done',
  'dropped',
]

/** Statuses that move an item off the dashboard and into the archive. */
export const CLOSED_STATUSES: readonly ItemStatus[] = ['done', 'dropped']

export interface User {
  id: number
  username: string
  created_at: string
  last_login_at: string | null
}

export interface CategoryBrief {
  id: number
  name: string
  color: string | null
}

export interface SubcategoryBrief {
  id: number
  name: string
  /** Free text set on the subcategory, shown alongside its heading. */
  status_note: string | null
  category: CategoryBrief
}

export interface Subcategory {
  id: number
  category_id: number
  name: string
  status_note: string | null
  sort_order: number
  is_archived: boolean
  created_at: string
  open_item_count: number
}

export interface Category {
  id: number
  name: string
  color: string | null
  sort_order: number
  is_archived: boolean
  created_at: string
  subcategories: Subcategory[]
}

export type IntervalType = 'daily' | 'weekly' | 'monthly' | 'custom_days'

export interface RecurrenceRule {
  id: number
  interval_type: IntervalType
  interval_count: number
  custom_days: number | null
  is_active: boolean
}

export interface RecurrenceInput {
  interval_type: IntervalType
  interval_count?: number
  custom_days?: number | null
}

export interface Tag {
  id: number
  name: string
  color: string | null
}

export interface Item {
  id: number
  title: string
  description: string | null
  status: ItemStatus
  /** Free text the user typed. Distinct from `status`, which is the workflow
   *  state. Null means show nothing at all. */
  status_note: string | null
  priority: ItemPriority
  /** Non-null means the item was dragged into place and outranks every other
   *  sort rule. The UI shows a marker so its position is never a mystery. */
  manual_rank: number | null
  /** A calendar date, not an instant: "due on the 20th", timezone-free. */
  due_date: string | null
  auto_escalated: boolean
  priority_before_escalation: ItemPriority | null
  subcategory: SubcategoryBrief
  tags: Tag[]
  checklist_total: number
  checklist_done: number
  recurrence_rule_id: number | null
  recurrence_rule: RecurrenceRule | null
  spawned_from_item_id: number | null
  /** The user has overruled an auto-escalation; the nightly pass leaves this
   *  item alone until its due date changes. */
  escalation_opt_out: boolean
  created_at: string
  updated_at: string
  started_at: string | null
  completed_at: string | null
  days_open: number
}

export interface ItemListResponse {
  items: Item[]
  /** Total matching the filters, before any limit -- what "showing 15 of 63"
   *  is counting. */
  total: number
}

export interface DashboardStats {
  open_total: number
  by_priority: Record<ItemPriority, number>
  overdue: number
  due_today: number
  due_this_week: number
  blocked: number
  completed_this_week: number
  oldest_open_days: number | null
  oldest_open_item_id: number | null
  oldest_open_title: string | null
}

export interface DashboardSummary {
  stats: DashboardStats
  item_limit: number
  escalation_threshold_days: number
  timezone: string
  /** The local date the server counted against, so the client agrees with it
   *  about what "today" means. */
  today: string
}

export interface ActivityEntry {
  id: number
  event_type: string
  field_name: string | null
  old_value: string | null
  new_value: string | null
  created_at: string
}

// --- request payloads -------------------------------------------------------

export interface ItemCreatePayload {
  subcategory_id: number
  title: string
  description?: string | null
  status?: ItemStatus
  status_note?: string | null
  priority?: ItemPriority
  due_date?: string | null
  tag_ids?: number[]
}

/** Every field optional. Omitting one leaves it alone; sending null clears it,
 *  which is a different thing and the API treats it as such. */
export interface ItemUpdatePayload {
  /** Absent leaves recurrence alone; explicit null turns it off. */
  recurrence?: RecurrenceInput | null
  subcategory_id?: number
  title?: string
  description?: string | null
  status?: ItemStatus
  /** Absent leaves it alone; null or blank clears it. */
  status_note?: string | null
  priority?: ItemPriority
  due_date?: string | null
  tag_ids?: number[]
}

export interface ItemQuery {
  subcategory_id?: number
  category_id?: number
  status?: ItemStatus[]
  priority?: ItemPriority[]
  tag_id?: number[]
  /** Named due-date window, evaluated server-side against the user's local
   *  today -- not the browser's, so the two never disagree. */
  due?: 'overdue' | 'today' | 'week' | 'month' | 'none'
  /** Free text over titles, descriptions and note bodies. */
  q?: string
  include_closed?: boolean
  limit?: number
  offset?: number
}

export interface TagWithCount extends Tag {
  item_count: number
}

export interface Note {
  id: number
  item_id: number
  body_html: string
  created_at: string
  updated_at: string
  /** True when updated_at has moved past created_at; drives the "edited" stamp. */
  edited: boolean
}

export interface ChecklistEntry {
  id: number
  item_id: number
  text: string
  is_done: boolean
  sort_order: number
  created_at: string
  completed_at: string | null
}

export interface AttachmentRead {
  id: number
  item_id: number | null
  note_id: number | null
  original_filename: string
  mime_type: string
  size_bytes: number
  created_at: string
  /** Authenticated route; the uploads directory is not web-accessible. */
  url: string
}

export interface ArchiveQuery {
  q?: string
  status?: ItemStatus
  category_id?: number
  subcategory_id?: number
  completed_from?: string
  completed_to?: string
  limit?: number
  offset?: number
}

export interface AppSettings {
  dashboard_item_limit: number
  escalation_threshold_days: number
  timezone: string
  /** The full subscribe-in-Outlook URL, secret included. Treat as a credential. */
  ics_url: string

  /* Invoice identity. Copied onto an invoice when it is raised, never read
     back from here afterwards -- editing an address leaves everything already
     sent exactly as it was sent. */
  invoice_from_name: string
  invoice_from_address: string
  invoice_short_name: string
  invoice_default_rate: string
  invoice_payment_terms: string
  invoice_beneficiary_name: string
  invoice_account_number: string
  invoice_routing_number: string
  invoice_bank_name: string
}

/** A note that belongs to nothing — reference material, a scratchpad. Distinct
 *  from `Note`, which is the running log of an item. */
export interface StandaloneNote {
  id: number
  title: string | null
  body_html: string
  created_at: string
  updated_at: string
  /** Never blank, so no row in the list is unclickable. */
  display_title: string
  /** Nothing typed yet; the client uses this to clean up an abandoned note. */
  is_empty: boolean
}

/** The list view. Carries no body: an index should not ship every word of
 *  every note. */
export interface StandaloneNoteSummary {
  id: number
  title: string | null
  created_at: string
  updated_at: string
  preview: string
  display_title: string
}

/** A subscribed calendar. The URL is deliberately absent: the server holds it
 *  and never sends it back, because a published calendar URL is a credential
 *  with no sign-in behind it. `url_hint` is enough to tell two feeds apart. */
export interface CalendarFeed {
  id: number
  name: string
  color: string | null
  is_enabled: boolean
  sort_order: number
  url_hint: string
  /** Last *successful* refresh, so a run of failures does not look fresh. */
  last_fetched_at: string | null
  last_status: 'ok' | 'error' | null
  last_error: string | null
  last_event_count: number
  /** Exact phrases. An event shows if its title contains any one of them;
   *  an empty list means show everything from this calendar. */
  keywords: string[]
  /** How many cached events the filter currently lets through, or null when
   *  there is no filter. Guards the failure this feature invites: a phrase
   *  that matches nothing hides the calendar with no clue why. */
  visible_event_count: number | null
}

export interface CalendarEvent {
  /** Row id — NOT an identity. Every refresh deletes and re-inserts the cache,
   *  so this changes for the same meeting. Use `uid` + `starts_at` to remember
   *  anything about an event across reads. */
  id: number
  feed_id: number
  /** ICS UID: stable across refreshes, shared by every occurrence of a
   *  recurring series. */
  uid: string | null
  summary: string | null
  location: string | null
  /** UTC, like every timestamp this API returns. */
  starts_at: string
  ends_at: string | null
  is_all_day: boolean
}

export interface CalendarDay {
  events: CalendarEvent[]
  feed_colors: Record<number, string | null>
  feed_names: Record<number, string>
  /** Separates "nothing scheduled" from "no calendar connected". */
  has_feeds: boolean
  oldest_refresh: string | null
  stale: boolean
}

export interface CalendarTestResult {
  ok: boolean
  message: string
  event_count: number
  next_event: string | null
}

export interface CalendarFeedPayload {
  name: string
  url: string
  color?: string | null
  is_enabled?: boolean
  keywords?: string[]
}


/* ==========================================================================
   Consulting

   The unit of everything here is the *day*: one client, one date, one rate,
   one activity line -- which is one row of the invoice. The individual
   start/stop `segments` sit inside it. A segment on its own has no client and
   no rate, so nothing in this API ever returns a bare list of them.
   ========================================================================== */

/** Joint venture is a rate, not the absence of one: it fills the Rate column,
 *  its hours count toward the hour total, and it has no money at all. */
export type RateKind = 'hourly' | 'joint_venture'

export interface BillTo {
  id: number
  name: string
  address: string | null
  /** The engagement, printed as "Re: ..." — belongs to the payer, and is
   *  copied onto each invoice so it can still be overridden per invoice. */
  re_line: string | null
  is_archived: boolean
  created_at: string
}

export interface ConsultingClient {
  id: number
  name: string
  default_rate_kind: RateKind
  /** Null means "whatever the application default is", which is where most
   *  clients stay. `effective_rate` has that already resolved. */
  default_rate: number | null
  default_bill_to_id: number | null
  is_archived: boolean
  created_at: string
  effective_rate: number | null
}

export interface TimeSegment {
  id: number
  day_id: number
  /** Naive UTC, like every timestamp from this API. */
  started_at: string
  /** Null means this is the running timer. */
  ended_at: string | null
  note: string | null
  hours: number
}

export interface WorkDay {
  id: number
  client_id: number
  client_name: string
  /** A plain calendar date in the app's timezone. Never parse it as an instant. */
  work_date: string
  rate_kind: RateKind
  rate: number | null
  /** What prints in the Activity cell, composed or hand-written. */
  activity: string
  activity_is_override: boolean
  hours: number
  /** Null for joint venture: an empty cell, not a zero. */
  amount: number | null
  /** Non-null means invoiced, which is the same thing as archived. */
  invoice_id: number | null
  segments: TimeSegment[]
}

export interface SummaryRow {
  client_name: string
  rate_kind: RateKind
  rate: number | null
  hours: number
  amount: number | null
}

export interface EntriesResponse {
  days: WorkDay[]
  summary: SummaryRow[]
  total_hours: number
  total_amount: number
}

export interface RunningTimer {
  segment: TimeSegment | null
  client_id: number | null
  client_name: string | null
  day_id: number | null
  work_date: string | null
  /** The rate the running stretch is accruing at, so an override chosen at
   *  Start is visible while it runs rather than only once it stops. */
  rate_kind: RateKind | null
  rate: number | null
}

export interface InvoiceLine {
  id: number
  work_date: string
  client_name: string
  activity: string | null
  rate_kind: RateKind
  rate: number | null
  hours: number
}

export interface ConsultingInvoice {
  id: number
  invoice_date: string
  period_label: string
  from_name: string
  from_address: string | null
  bill_to_name: string
  bill_to_address: string | null
  re_line: string | null
  payment_terms: string | null
  beneficiary_name: string | null
  account_number: string | null
  routing_number: string | null
  bank_name: string | null
  total_hours: number
  total_amount: number
  created_at: string
  line_count: number
  /** How many of its rows are still archived against it. Zero means every one
   *  has been released back to the open list; the invoice itself is unchanged. */
  archived_day_count: number
  lines: InvoiceLine[]
}

export type EntryFilter = 'open' | 'invoiced' | 'all'

export interface EntriesQuery {
  start?: string
  end?: string
  client_id?: number
  archived?: EntryFilter
}
